import * as z from "zod";

/** Per-bot set. Missing values belong to the original renderer. */
export const BotAvatarSetSchema = z.enum(["organic", "rakazo"]);
export type BotAvatarSet = z.infer<typeof BotAvatarSetSchema>;
export const DEFAULT_NEW_BOT_AVATAR_SET: BotAvatarSet = "organic";

/**
 * The sets the pickers offer. The "rakazo" set draws upstream's logo, which a Nurlings build must
 * not show, so only the original shapes are offered (stored values still render as stored).
 */
export const SELECTABLE_BOT_AVATAR_SETS: readonly BotAvatarSet[] = ["organic"];

export function resolveBotAvatarSet(value?: string | null): BotAvatarSet {
  return value === "rakazo" ? "rakazo" : "organic";
}

/**
 * `#hex::shape_N` indexes: 0 to 7 are retired static shapes that now draw the bot's own organic
 * body, 8 is the brand blob, 9 to 11 are the original organic bodies in `@rakazo/core` and 12 to 21 pin
 * organic family 0 to 9. Older builds read `N % 12`, so they draw a different body with the right color.
 */
export const BOT_AVATAR_SHAPE_COUNT = 22;

/**
 * 256x256 webp data URLs from Avatar Studio stay well under this. Cap writes so
 * a pasted raw photo cannot bloat every bot list payload.
 */
export const BOT_AVATAR_VALUE_MAX_LENGTH = 200_000;

const HEX_COLOR = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const SHAPE_SUFFIX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})::shape_\d+$/;
const DATA_IMAGE = /^data:image\/(?:png|jpeg|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/;

export type ParsedBotAvatar =
  | { kind: "image"; imageUrl: string }
  | { kind: "shape"; color: string; shapeIndex: number }
  | { kind: "color"; color: string }
  | { kind: "other"; raw: string };

export function parseBotAvatarValue(raw: string): ParsedBotAvatar {
  if (raw.startsWith("data:image/")) {
    return { kind: "image", imageUrl: raw };
  }
  if (raw.includes("::shape_")) {
    const parts = raw.split("::shape_");
    const color = parts[0] || "#F97316";
    const rawShape = parts[1] ?? "0";
    const parsedShape = /^\d+$/.test(rawShape) ? Number(rawShape) : 0;
    const shapeIndex = Number.isSafeInteger(parsedShape) ? parsedShape % BOT_AVATAR_SHAPE_COUNT : 0;
    return { kind: "shape", color, shapeIndex };
  }
  if (HEX_COLOR.test(raw)) return { kind: "color", color: raw };
  return { kind: "other", raw };
}

/** True for values we persist on `bots.color` (hex, encoded shape, or data image). */
export function isBotAvatarValue(raw: string): boolean {
  if (!raw || raw.length > BOT_AVATAR_VALUE_MAX_LENGTH) return false;
  return HEX_COLOR.test(raw) || SHAPE_SUFFIX.test(raw) || DATA_IMAGE.test(raw);
}

export const BotAvatarValueSchema = z
  .string()
  .min(1)
  .max(BOT_AVATAR_VALUE_MAX_LENGTH)
  .refine(isBotAvatarValue, { message: "Invalid bot avatar" });
